import React, { useId } from "react";
import { AlertCircle, CheckCircle2, Clock3, Loader2, PlusCircle, ShieldAlert, Sparkles } from "lucide-react";

import PatternSections, { ScreenIntro, Section } from "../PatternSections";
import MatchSections from "./MatchSections";
import { ACTION, RESULT } from "./editorModel";
import { TEXT } from "./editorText";
import { MATCH_STATUS, categoryIssues, patternVerdict } from "./matchModel";

const P = TEXT.previewScreen;
const M = TEXT.match;
const ICON_SIZE = 16;

/** The pattern verdict banner (shared with the Learning Catalog's detail popup): existing pattern or new, with one line under it. */
export function VerdictBanner({ existing, title, hint }) {
  const Icon = existing ? Sparkles : PlusCircle;
  return (
    <div className={`le-verdict ${existing ? "le-verdict--existing" : "le-verdict--new"}`}>
      <p className="le-verdict-title"><Icon size={ICON_SIZE} aria-hidden="true" />{title}</p>
      {hint && <p className="le-verdict-sub">{hint}</p>}
    </div>
  );
}

/**
 * One compact banner for the whole match: progress while matching (with an indeterminate bar), the failure with Retry, or
 * the pattern verdict (existing pattern / new pattern) with the one note about what Arta writes from these lines.
 */
function MatchBanner({ match, onRetry }) {
  if (match.status === MATCH_STATUS.LOADING) {
    return (
      <div className="le-verdict le-verdict--busy" role="status" aria-live="polite">
        <p className="le-verdict-title"><Loader2 size={ICON_SIZE} className="le-spin" aria-hidden="true" />{M.matchingTitle}</p>
        <div className="le-progress" aria-hidden="true"><span className="le-progress-bar" /></div>
        <p className="le-verdict-sub">{M.matchingHint}</p>
      </div>
    );
  }
  if (match.status === MATCH_STATUS.ERROR) {
    return (
      <div className="le-verdict le-verdict--error" role="alert">
        <p className="le-verdict-title"><AlertCircle size={ICON_SIZE} aria-hidden="true" />{M.failedTitle}</p>
        <p className="le-verdict-sub">{M.failedBody}</p>
        <div className="le-actions"><button type="button" className="le-btn le-btn--sm" onClick={onRetry}>{M.retry}</button></div>
      </div>
    );
  }
  const verdict = patternVerdict(match);
  if (!verdict) return null;
  return (
    <VerdictBanner existing={verdict.existing} title={verdict.existing ? M.existingPattern(verdict.label) : M.newPattern}
      hint={verdict.existing ? P.note : M.newPatternHint} />
  );
}

/** Issues about the category itself (no line): a warning, or a blocking error that is why Save is off. */
function CategoryIssues({ match }) {
  return categoryIssues(match).map((issue, i) => (issue.blocking
    ? <p key={`${issue.code}-${i}`} className="le-error" role="alert">{`${issue.message} ${M.blocking}`}</p>
    : <p key={`${issue.code}-${i}`} className="le-note le-note--blocked">{issue.message}</p>));
}

/** What was left out of the draft as too short. */
function Dropped({ dropped }) {
  if (!dropped.length) return null;
  return (
    <Section label={P.dropped} className="le-dropped">
      <ul className="le-list">
        {dropped.map((d, i) => <li key={`${d.section}-${i}`} className="le-muted">{`${d.text} (${d.section}: ${d.reason})`}</li>)}
      </ul>
    </Section>
  );
}

/** What would be learned: every line with its match (when matching is on) or as a read-only summary, plus what was left out. */
export function PreviewScreen({ state, blocked, dispatch }) {
  const id = useId();
  const { draft, dropped, match } = state.preview;
  const matching = match.status !== MATCH_STATUS.IDLE;
  const onPick = (section, index, decision, selectedId) =>
    dispatch({ type: ACTION.PICK_MATCH, section, index, decision, selected_id: selectedId });
  const onRetry = () => dispatch({ type: ACTION.MATCH_START, key: match.key });
  const fixKindLabel = TEXT.fixKinds[draft.fixKind];
  return (
    <section className="le-screen" aria-labelledby={`${id}-h`}>
      <ScreenIntro id={`${id}-h`} heading={P.heading} meta={P.hint} />
      {blocked && (
        <p className={`le-note ${blocked === "recording" ? "le-note--waiting" : "le-note--blocked"}`}
          role={blocked === "recording" ? "status" : "alert"} aria-live="polite">
          {TEXT.eligibility[blocked] || TEXT.eligibility.fallback}
        </p>
      )}
      {!matching && <p className="le-note">{P.note}</p>}
      {matching && <CategoryIssues match={match} />}
      {matching && <MatchBanner match={match} onRetry={onRetry} />}
      {matching ? (
        <MatchSections category={state.category.label} draft={draft} fixKindLabel={fixKindLabel} match={match} onPick={onPick} onRetry={onRetry}>
          <Dropped dropped={dropped} />
        </MatchSections>
      ) : (
        <PatternSections labels={P} category={state.category.label} symptoms={draft.symptoms} steps={draft.steps}
          rootCause={draft.rootCause} fixKindLabel={fixKindLabel} fixText={draft.fixText}>
          <Dropped dropped={dropped} />
        </PatternSections>
      )}
    </section>
  );
}

/** Learned, sent for review, or refused: the outcome, with its reason. */
export function ResultScreen({ result }) {
  const text = TEXT.result[result.kind];
  if (result.kind === RESULT.REFUSED || !text) {
    return (
      <section className="le-result le-result--refused" role="alert">
        <ShieldAlert size={22} aria-hidden="true" />
        <h3 className="le-h">{TEXT.refused.heading}</h3>
        <p>{TEXT.refused[result.reason] || TEXT.refused.fallback}</p>
      </section>
    );
  }
  const Icon = result.kind === RESULT.LEARNED ? CheckCircle2 : Clock3;
  return (
    <section className="le-result" role="status" aria-live="polite">
      <Icon size={22} aria-hidden="true" />
      <h3 className="le-h">{text.heading}</h3>
      <p>{text.body}</p>
    </section>
  );
}
